import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { transformRoute, useTranslate } from "helpers";
import { ContactRepresentativeSearchPageCmp, ContactRepresentativeSearchPageProps } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Routes, Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import TabMenu from "ui/modules/contacts/pages/components/TabMenu/TabMenu";
import { ContactDetailFormValues, ContactRelationRow } from "services/api/contacts/types";
import {
  getContactAPI,
  getContactRelationsAPI,
  deleteContactRelationAPI,
} from "services/api/contacts/api";
import { Dialog } from "ui/components/Dialog";
import { initialContactDetailForm } from "services/api/contacts/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_CONTACT } from "app/components/Sidebar/consts";

import ContactRepresentativeSearchResults from "./components/ContactRepresentativeSearchResults/ContactRepresentativeSearchResults";
import ContactRepresentativeSearchFilters from "./components/ContactRepresentativeSearchFilters/ContactRepresentativeSearchFilters";
import ContactRepresentativeEditCard from "../ContactRepresentativeEditCard/ContactRepresentativeEditCard";

const ContactRepresentativeSearchPage: ContactRepresentativeSearchPageCmp = (
  props: ContactRepresentativeSearchPageProps
) => {
  const { id } = useParams();
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState<ContactDetailFormValues>(initialContactDetailForm);
  const [relations, setRelations] = useState<ContactRelationRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showAddNewModal, setShowAddNewModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchContact(id);
      fetchRelations(id);
    }
  }, [id]);

  const fetchContact = async (contactId: string) => {
    try {
      const result = await getContactAPI(contactId);
      setFormValues(result);
    } catch {}
  };

  const fetchRelations = async (contactId: string) => {
    setIsLoading(true);
    try {
      const result = await getContactRelationsAPI(contactId);
      setRelations(result);
    } catch {
      setRelations([]);
    }
    setIsLoading(false);
  };

  const filteredRelations = useMemo(() => {
    if (!searchText) return relations;
    const lower = searchText.toLowerCase();
    return relations.filter((r) => {
      const name = `${r.personContact?.firstName ?? ""} ${r.personContact?.lastName ?? ""}`.toLowerCase();
      const job = (r.personContact?.jobTitle ?? "").toLowerCase();
      const dept = (r.personContact?.department ?? "").toLowerCase();
      return name.includes(lower) || job.includes(lower) || dept.includes(lower);
    });
  }, [relations, searchText]);

  const handlePersonClick = (personContactId: string) => {
    navigate(transformRoute(ContactRoutes.ContactDetailPageRouter, { id: personContactId }));
  };

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, relationId: string) => {
      event.stopPropagation();
      event.preventDefault();
      setDeleteId(relationId);
      setOpenDialog(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!id || !deleteId) return;
    await deleteContactRelationAPI(id, deleteId);
    setOpenDialog(false);
    setDeleteId(null);
    fetchRelations(id);
  }, [id, deleteId]);

  const handleDeleteClose = useCallback(() => {
    setDeleteId(null);
    setOpenDialog(false);
  }, []);

  const handleAddNew = useCallback(() => {
    setShowAddNewModal(true);
  }, []);

  const handleSearchChange = useCallback((search: string) => {
    setSearchText(search);
  }, []);

  const handleEditSuccess = useCallback(() => {
    if (id) fetchRelations(id);
    setShowAddNewModal(false);
  }, [id]);

  const breadcrumbs: BreadcrumbItem[] = [
    { text: SIDEBAR_ITEM_CONTACT.text, route: SIDEBAR_ITEM_CONTACT.route },
  ];

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  {formValues.pictures && formValues.pictures.length > 0 && (
                    <img src={formValues.pictures[0].url} alt={formValues.companyName ?? ""} title={formValues.companyName ?? ""} className="h-12 w-12 object-contain rounded-full" />
                  )}
                  <div>
                    <PageTitle
                      title={`Povezane osobe [${formValues.companyName || `${formValues.firstName} ${formValues.lastName}`}]`}
                      icon=""
                      iconColor=""
                    />
                    <Breadcrumb items={breadcrumbs} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center">
              </div>
            </div>
          </PageHeader>
          <TabMenu contactId={id} contactTypeId={formValues?.contactType?.id} />
        </div>
        <div className="px-4 py-4">
          <ContactRepresentativeSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />

          {!isLoading && filteredRelations.length === 0 ? (
            <NoResults message="Nema povezanih osoba" />
          ) : (
            <ContactRepresentativeSearchResults
              data={filteredRelations}
              isLoading={isLoading}
              onPersonClick={handlePersonClick}
              onDeleteClick={handleDeleteClick}
              onRowClick={(rowId) => {
                const rel = relations.find(r => r.id === rowId);
                if (rel?.personContact?.id) handlePersonClick(rel.personContact.id);
              }}
            />
          )}
        </div>
      </div>
      <ContactRepresentativeEditCard
        contactId={id}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title="Uklanjanje predstavnika"
        message="Jeste li sigurni da želite ukloniti ovog predstavnika?"
        confirmText="Ukloni"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteClose}
      />
    </>
  );
};

ContactRepresentativeSearchPage.route = Routes.ContactRepresentativeSearchPageRouter;

export default ContactRepresentativeSearchPage;
